import { HttpClient } from "@angular/common/http";
import { Injectable, Injector } from "@angular/core";
import { FeaturePermissionName } from "@common/ADAPT.Common.Model/embed/feature-permission-name.enum";
import { UserType } from "@common/ADAPT.Common.Model/embed/user-type";
import { Board, BoardBreezeModel } from "@common/ADAPT.Common.Model/organisation/board";
import { Connection, ConnectionBreezeModel } from "@common/ADAPT.Common.Model/organisation/connection";
import { Role, RoleBreezeModel } from "@common/ADAPT.Common.Model/organisation/role";
import { RoleConnection, RoleConnectionBreezeModel } from "@common/ADAPT.Common.Model/organisation/role-connection";
import { RoleType, RoleTypeBreezeModel } from "@common/ADAPT.Common.Model/organisation/role-type";
import { RoleTypeCode } from "@common/ADAPT.Common.Model/organisation/role-type-code";
import { Team, TeamBreezeModel } from "@common/ADAPT.Common.Model/organisation/team";
import { TeamLocation, TeamLocationBreezeModel } from "@common/ADAPT.Common.Model/organisation/team-location";
import { TeamSupplementaryDataBreezeModel } from "@common/ADAPT.Common.Model/organisation/team-supplementary-data";
import { TeamType } from "@common/ADAPT.Common.Model/organisation/team-type";
import { TeamTypeCode } from "@common/ADAPT.Common.Model/organisation/team-type-code";
import { TeamTypeConfiguration, TeamTypeConfigurationBreezeModel } from "@common/ADAPT.Common.Model/organisation/team-type-configuration";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { ServiceUri } from "@common/configuration/service-uri";
import { FeatureService } from "@common/feature/feature.service";
import { ActiveEntityUtilities } from "@common/lib/data/active-entity-utilities";
import { BreezePredicateUtilities } from "@common/lib/data/breeze-predicate-utilities";
import { MethodologyPredicate } from "@common/lib/data/methodology-predicate";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { ColourUtilities } from "@common/lib/utilities/colour-utilities";
import { ObjectUtilities } from "@common/lib/utilities/object-utilities";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { UserService } from "@common/user/user.service";
import { IColourPickerPresetGroups } from "@common/ux/colour-picker/colour-picker.component";
import { ICreateTeamBindingModel } from "@org-common/lib/teams/create-team-binding-model.interface";
import moment from "moment";
import { forkJoin, from, interval, lastValueFrom, MonoTypeOperatorFunction, Observable, of, tap, timer } from "rxjs";
import { defaultIfEmpty, filter, map, startWith, switchMap, take, takeUntil } from "rxjs/operators";
import { IntegratedArchitectureFrameworkQueryUtilities } from "../architecture/integrated-architecture-framework-query-utilities";
import { AuthorisationNotificationService } from "../authorisation/authorisation-notification.service";
import { DirectorySharedService } from "../directory-shared/directory-shared.service";
import { FeaturesService } from "../features/features.service";
import { LabellingService } from "../labelling/labelling.service";
import { AfterOrganisationInitialisation, AfterOrganisationInitialisationObservable } from "../organisation/after-organisation-initialisation.decorator";
import { BaseOrganisationService } from "../organisation/base-organisation.service";
import { OrganisationService } from "../organisation/organisation.service";


@Injectable({
    providedIn: "root",
})
export class CommonTeamsService extends BaseOrganisationService {
    private static readonly TeamMemberRoleTypeCodes = [
        RoleTypeCode.TeamLeader,
        RoleTypeCode.TeamMember,
        RoleTypeCode.TeamParticipant,
    ];

    private teamMemberRoleType!: RoleType;
    private teamLeaderRoleType!: RoleType;

    private archData = new IntegratedArchitectureFrameworkQueryUtilities(this.commonDataService);

    public constructor(
        injector: Injector,
        private authNotificationService: AuthorisationNotificationService,
        private directorySharedService: DirectorySharedService,
        private labellingService: LabellingService,
        private userService: UserService,
        private organisationService: OrganisationService,
        private httpClient: HttpClient,
        private featureService: FeatureService,
        private featuresService: FeaturesService,
    ) {
        super(injector);
    }

    private static isActiveTeamMemberRoleConnectionForPerson(roleConnection: RoleConnection, personId: number) {
        const roleTypeCode = roleConnection?.role?.roleType?.code;
        return roleConnection?.connection?.personId === personId
            && ActiveEntityUtilities.isActive(roleConnection)
            && !!roleTypeCode
            && CommonTeamsService.TeamMemberRoleTypeCodes.includes(roleTypeCode);
    }

    public get waitForInitialisation$() {
        return this.waitUntilOrganisationInitialised();
    }

    protected organisationInitialisationActions() {
        return [
            this.initialiseService(),
        ];
    }

    protected initialiseService() {
        return forkJoin([
            this.commonDataService.getAll(RoleTypeBreezeModel),
            // needs to be primed so team metadata is present
            this.promiseToGetTeamTypeConfigurations(),
        ]).pipe(
            tap(([allRoleTypes]) => {
                this.teamLeaderRoleType = assertGetRoleType(allRoleTypes, RoleTypeCode.TeamLeader);
                this.teamMemberRoleType = assertGetRoleType(allRoleTypes, RoleTypeCode.TeamMember);
            }),
        );

        function assertGetRoleType(roleTypes: RoleType[], code: RoleTypeCode) {
            const roleType = roleTypes.find((rt) => rt.code === code);

            if (!roleType) {
                throw new Error(`Unable to find ${code} roleType`);
            }

            return roleType;
        }
    }

    public get teamMemberLabelPlural() {
        return this.teamMemberRoleType.defaultLabelPlural;
    }

    public get teamLeaderLabel() {
        return this.teamLeaderRoleType.defaultLabel;
    }

    @AfterOrganisationInitialisationObservable
    public getTeamLeaderLabel$() {
        return of(this.teamLeaderLabel);
    }

    @AfterOrganisationInitialisationObservable
    public getTeamMemberLabelPlural$() {
        return of(this.teamMemberLabelPlural);
    }

    public getTeamById(teamId: number, forceRemote?: boolean) {
        return this.commonDataService.getById(TeamBreezeModel, teamId, forceRemote);
    }

    public promiseToGetAllTeams() {
        return lastValueFrom(this.commonDataService.getAll(TeamBreezeModel).pipe(
            this.primeTeamsProperties(),
        ));
    }

    public async promiseToGetTeamTypeConfigurations() {
        return lastValueFrom(this.commonDataService.getAll(TeamTypeConfigurationBreezeModel).pipe(
            map((teamTypeConfigurations) => teamTypeConfigurations
                .sort(SortUtilities.getSortByFieldFunction<TeamTypeConfiguration>("ordinal", "name")))
        ));
    }

    public getTeamTypeConfigurationByCode(code: TeamTypeCode) {
        const predicate = new MethodologyPredicate<TeamTypeConfiguration>("code", "==", code);
        return this.commonDataService.getByPredicate(TeamTypeConfigurationBreezeModel, predicate).pipe(
            map((config) => config.length > 0 ? config[0] : undefined)
        );
    }

    public getTeamsByTeamTypeConfiguration(teamTypeConfiguration: TeamTypeConfiguration) {
        const predicate = new MethodologyPredicate<Team>("teamTypeConfigurationId", "==", teamTypeConfiguration.teamTypeConfigurationId);
        return this.commonDataService.getByPredicate(TeamBreezeModel, predicate);
    }

    public promiseToGetLeadershipTeam() {
        const predicate = new MethodologyPredicate<Team>("teamType", "==", TeamType.Leadership);
        return lastValueFrom(this.commonDataService.getByPredicate(TeamBreezeModel, predicate).pipe(
            map((teams) => teams[0]),
        ));
    }

    public async promiseToGetLeadershipTeamWithFallback() {
        let team = await this.promiseToGetLeadershipTeam();
        if (!team) {
            // this shouldn't happen in normal self-led, but can probably happen in QA env.
            const teams = await this.promiseToGetActiveTeamsForCurrentPerson();
            team = teams[0];
        }

        return team;
    }

    public createTeam(bindingModel: ICreateTeamBindingModel) {
        const uri = `${ServiceUri.MethodologyServicesServiceBaseUri}/CreateTeam`;
        return this.httpClient.post<number>(uri, bindingModel).pipe(
            switchMap((teamId) => this.primeTeam(teamId)),
        );
    }

    private primeTeam(teamId: number) {
        // don't want to force remote and let entity sync fetch first as entity sync will fetch all the related entities in primeAfterFetch
        const getTeam$ = interval(300).pipe(
            startWith(0),
            switchMap(() => this.getTeamById(teamId)),
            filter((team) => !!team),
            take(1),
        );
        return getTeam$.pipe(
            takeUntil(timer(1000)), // 1s max then only we will force remote (that means signalr bogs) - need force remote as there was a getAll before
            defaultIfEmpty(undefined),
            switchMap((team) => team ? of(team) : this.getTeamById(teamId, true)),
            // feature status for the team will be updated through entity sync which is done in a background queue.
            // simply get feature status for the new team, if any, then entity sync has gone through
            switchMap((team) => this.waitForFeatureStatuses(team!)),
            tap(() => {
                this.authNotificationService.notifyAuthorisationChanged();
                this.authNotificationService.refreshPermissions();
            }),
        );
    }

    private waitForFeatureStatuses(team: Team) {
        // Poll every 0.5s until non-empty; startWith(0) makes first call immediate
        const poll$ = interval(500).pipe(
            startWith(0),
            switchMap(() => this.featureService.getFeatureStatusesForTeam(team.teamId)),
            filter((statuses) => !!statuses?.length),
            take(1),
        );

        return poll$.pipe(
            takeUntil(timer(3000)), // won't wait forever, 3s max and then we will do the manual priming
            defaultIfEmpty([]),
            switchMap((statuses) => {
                // both leaders and owner teams will have a default team board - if the board didn't get through, still need to prime
                // - main reason for board not getting through is the related entity (Team), if getTeamById is slow, added board won't be fetched!
                // - won't be issue with other sessions where primeAfterFetch for team will take care of it
                if (statuses.length && team.boards?.length) {
                    return of(team);
                }

                // fallback - manually prime just in case entity sync not working - at least the session that creates the team will still be functional
                return forkJoin([
                    this.featuresService.updateFeatureStatusesForTeam(team.teamId),
                    this.archData.primeRolesForTeamId(team.teamId),
                    // can't use kanban service here as that will cause dependencies injection issues
                    this.commonDataService.getByPredicate(BoardBreezeModel, new MethodologyPredicate<Board>("teamId", "==", team.teamId), true),
                    this.getTeamLocations(team, true),
                ]).pipe(
                    map(() => team),
                );
            }),
        );
    }

    public async promiseToCreateTeam() {
        const organisationId = this.organisationService.getOrganisationId();

        const teamTypeConfigs = await this.promiseToGetTeamTypeConfigurations();
        const standardTeamConfig = teamTypeConfigs.find((t) => t.code === TeamTypeCode.Team);
        if (!standardTeamConfig) {
            throw new Error("Team reference data not found!");
        }

        return lastValueFrom(this.commonDataService.create(TeamBreezeModel, {
            organisationId,
            startDate: moment.utc().toDate(),
            isPrivate: false,
            allowObjectivesTeamRead: true,
            teamType: TeamType.Standard,
            teamTypeConfiguration: standardTeamConfig,
        }));
    }

    public promiseToCreateTeamRoleConnection(team: Team, role: Role) {
        const defaults: Partial<RoleConnection> = {
            team,
            role,
        };
        return lastValueFrom(this.archData.createRoleConnection(defaults));
    }

    public async promiseToAddTeamInput(team: Team, connection: Connection) {
        const roleConnection = await this.promiseToAddTeamRole(team, RoleTypeCode.TeamInput);
        roleConnection.connection = connection;
        if (connection.startDate > new Date()) {
            // use connection startDate if that's in the future
            roleConnection.startDate = connection.startDate;
        }
        return roleConnection;
    }

    public async promiseToAddTeamMember(team: Team, connection: Connection, userType: UserType) {
        const roleConnection = await (userType === UserType.Viewer
            ? this.promiseToAddTeamRole(team, RoleTypeCode.TeamParticipant)
            : this.promiseToAddTeamRole(team, RoleTypeCode.TeamMember));
        roleConnection.connection = connection;
        if (connection.startDate > new Date()) {
            // use connection startDate if that's in the future
            roleConnection.startDate = connection.startDate;
        }
        return roleConnection;
    }

    public async promiseToAddTeamLeader(team: Team, connection: Connection) {
        const roleConnection = await this.promiseToAddTeamRole(team, RoleTypeCode.TeamLeader);
        roleConnection.connection = connection;
        if (connection.startDate > new Date()) {
            roleConnection.startDate = connection.startDate;
        }
        return roleConnection;
    }

    private async promiseToAddTeamRole(team: Team, teamRoleTypeCode: RoleTypeCode) {
        const role = await this.promiseToGetTeamRoleByRoleTypeCode(team.teamId, teamRoleTypeCode);
        if (!role) {
            throw new Error("Team role failed to fetch");
        }

        return this.promiseToCreateTeamRoleConnection(team, role);
    }

    private primeTeamsProperties(): MonoTypeOperatorFunction<Team[]> {
        return ((source: Observable<Team[]>) => {
            return source.pipe(
                // priming teamLocations, teamLeaderPerson and label location since we deny $expand for Teams
                // to prevent private team leaks.
                // don't have to prime parent and childTeams as they are all covered by getAll(TeamBreezeModel)
                switchMap((teams) => forkJoin([
                    this.getAllTeamLocations(),
                    this.promiseToGetTeamTypeConfigurations(),
                ]).pipe(
                    map(() => teams),
                )),
                switchMap((teams) => {
                    // only prime people we haven't fetched already (in most cases they should all be fetched)
                    const leaderPersonIds = teams
                        .filter((team) => team.teamLeaderPerson === null && team.teamLeaderPersonId)
                        .map((team) => team.teamLeaderPersonId) as number[];
                    return this.directorySharedService.primePeopleWithIds(ArrayUtilities.distinct(leaderPersonIds)).pipe(
                        map(() => teams),
                    );
                }),
                switchMap((teams) => this.labellingService.primeLabelLocationsForAllTeams().pipe(
                    map(() => teams),
                )),
            );
        });
    }

    public async promiseToGetAllActiveTeams() {
        const teams = await lastValueFrom(this.commonDataService.getActive(TeamBreezeModel).pipe(
            this.primeTeamsProperties(),
        ));
        return teams.sort((a, b) => a.name.localeCompare(b.name));
    }

    public getChildTeamLocations(team: Team) {
        const predicate = new MethodologyPredicate<TeamLocation>("parentTeamId", "==", team.teamId);
        return this.commonDataService.getWithOptions(TeamLocationBreezeModel, predicate.getKey(TeamLocationBreezeModel.identifier), {
            predicate,
            orderBy: "ordinal ASC",
            navProperty: "team",
        });
    }

    public getActiveChildTeamLocations(team: Team) {
        const predicate = new MethodologyPredicate<TeamLocation>("parentTeamId", "==", team.teamId)
            .and(BreezePredicateUtilities.getIsActivePredicateByPath("team"));
        return this.commonDataService.getWithOptions(TeamLocationBreezeModel, predicate.getKey(TeamLocationBreezeModel.identifier), {
            predicate,
            orderBy: "ordinal ASC",
            navProperty: "team",
            namedParams: {
                activeOnly: true,
            },
        });
    }

    public getActiveChildTeams(team: Team) {
        return this.getActiveChildTeamLocations(team).pipe(
            map((teamLocations) => teamLocations
                .sort(SortUtilities.getSortByFieldFunction<TeamLocation>("ordinal"))
                .map((tl) => tl.team)),
        );
    }

    public getActiveParentTeamLocations(team: Team) {
        const predicate = new MethodologyPredicate<TeamLocation>("teamId", "==", team.teamId)
            .and(BreezePredicateUtilities.getIsActivePredicateByPath("parentTeam"));
        return this.commonDataService.getWithOptions(TeamLocationBreezeModel, predicate.getKey(TeamLocationBreezeModel.identifier), {
            predicate,
            orderBy: "ordinal ASC",
            namedParams: {
                activeOnly: true,
            },
        });
    }

    public getTeamLocations(team: Team, forceRemote = false) {
        const predicate = new MethodologyPredicate<TeamLocation>("teamId", "==", team.teamId);
        return this.commonDataService.getWithOptions(TeamLocationBreezeModel, predicate.getKey(TeamLocationBreezeModel.identifier), {
            predicate,
            forceRemote,
        });
    }

    public getAllLocationsForTeam(team: Team, forceRemote = false) {
        const predicate = new MethodologyPredicate<TeamLocation>("teamId", "==", team.teamId)
            .or(new MethodologyPredicate("parentTeamId", "==", team.teamId));
        return this.commonDataService.getWithOptions(TeamLocationBreezeModel, predicate.getKey(TeamLocationBreezeModel.identifier), {
            predicate,
            forceRemote,
        });
    }

    public getLocationItems(location: Partial<TeamLocation>, includeInactiveTeams = false) {
        return this.getAllTeamLocations().pipe(
            map((teamLocations) => teamLocations
                .filter((loc) => loc.team && (includeInactiveTeams || loc.team.isActive())
                    && (ObjectUtilities.isNullOrUndefined(location.zone)
                        ? ObjectUtilities.isNullOrUndefined(loc.zone)
                        : loc.zone === location.zone)
                    && (ObjectUtilities.isNullOrUndefined(location.parentTeamId)
                        ? ObjectUtilities.isNullOrUndefined(loc.parentTeamId)
                        : loc.parentTeamId === location.parentTeamId))
                .sort(SortUtilities.getSortByFieldFunction<TeamLocation>("ordinal"))),
        );
    }

    public getAllTeamLocations() {
        return this.commonDataService.getAll(TeamLocationBreezeModel);
    }

    public getAllTeamSupplementaryData() {
        return this.commonDataService.getAll(TeamSupplementaryDataBreezeModel);
    }

    public async promiseToGetTeamRoleByRoleTypeCode(teamId: number, roleTypeCode: RoleTypeCode) {
        const predicate = new MethodologyPredicate<Role>("teamId", "==", teamId)
            .and(new MethodologyPredicate<Role>("roleType.code", "==", roleTypeCode));

        const teamRole = await lastValueFrom(this.commonDataService.getByPredicate(RoleBreezeModel, predicate));
        return ArrayUtilities.getSingleFromArray(teamRole);
    }

    @AfterOrganisationInitialisation
    public async promiseToGetActiveTeamsForCurrentPerson() {
        const person = await this.userService.getCurrentPerson();
        return this.promiseToGetActiveTeamsForPersonId(person!.personId);
    }

    @AfterOrganisationInitialisation
    public promiseToGetActiveOwnersTeamsForCurrentPerson() {
        return this.promiseToGetActiveOwnersTeamsForPersonId(this.userService.getCurrentPersonId()!);
    }

    public getActiveTeamIdsForCurrentPerson() {
        return from(this.promiseToGetActiveTeamsForCurrentPerson()).pipe(
            map((teams) => teams.map((i) => i.teamId)),
        );
    }

    @AfterOrganisationInitialisation
    public async promiseToGetActiveTeamsForPersonId(personId: number) {
        await lastValueFrom(this.archData.getRoleConnectionsForPersonId(personId, true)); // prime from before
        const teams = await this.promiseToGetAllActiveTeams();
        return teams.filter((team) =>
            team.roleConnections.some((rc) => CommonTeamsService.isActiveTeamMemberRoleConnectionForPerson(rc, personId)));
    }

    @AfterOrganisationInitialisation
    public async promiseToGetActiveOwnersTeamsForPersonId(personId: number) {
        await lastValueFrom(this.archData.getRoleConnectionsForPersonId(personId, true)); // prime from before
        const ownerTeams = await this.promiseToGetActiveOwnerTeams();
        return ownerTeams.filter((team) =>
            team.roleConnections.some((rc) => CommonTeamsService.isActiveTeamMemberRoleConnectionForPerson(rc, personId)));
    }

    @AfterOrganisationInitialisation
    public async promiseToGetActiveOwnerTeams() {
        const teams = await this.promiseToGetAllActiveTeams();
        return teams.filter((team) => team.teamType === TeamType.Owner);
    }

    public async canAccessOwnerTab() {
        const myActiveOwnerTeams = await this.promiseToGetActiveOwnersTeamsForCurrentPerson();
        if (myActiveOwnerTeams.length > 0) {
            return true;
        }

        if (this.userService.currentPerson?.isCoach() || this.userService.currentPerson?.isStakeholderManager) {
            const allActiveOwnerTeams = await this.promiseToGetActiveOwnerTeams();
            return allActiveOwnerTeams.length > 0;
        }

        return false;
    }

    public async promiseToGetAllTeamMembers(team: Team) {
        const roleConnections = await this.promiseToGetTeamMemberRoleConnections(team);
        return this.getUniqueRoleConnectionPeople(roleConnections);
    }

    public getLatestTeamRoleConnections(team: Team) {
        const predicate = new MethodologyPredicate<RoleConnection>("teamId", "==", team.teamId)
            .and(new MethodologyPredicate<RoleConnection>("endDate", "==", team.endDate));
        return this.getRoleConnectionsFromActiveTeamsByPredicate(predicate);
    }

    public getActiveTeamMembers(teamId: number) {
        return this.getTeamById(teamId).pipe(
            switchMap((team) => this.promiseToGetActiveTeamMembers(team!)),
        );
    }

    public async promiseToGetActiveTeamMembers(team: Team) {
        const roleConnections = await this.promiseToGetTeamMemberRoleConnections(team, true);
        return this.getUniqueRoleConnectionPeople(roleConnections);
    }

    public async promiseToGetTeamMemberRoleConnections(team: Team, activeOnly?: boolean, includeTeamInputs = false) {
        const predicate = new MethodologyPredicate<RoleConnection>("teamId", "==", team.teamId);
        if (!includeTeamInputs) {
            predicate.and(new MethodologyPredicate<RoleConnection>("role.roleType.code", "!=", RoleTypeCode.TeamInput));
        }

        const roleConnections = await lastValueFrom(this.getRoleConnectionsFromActiveTeamsByPredicate(predicate));
        return roleConnections.filter((r) => r.isActive() || !activeOnly);
    }

    public async promiseToGetTeamLeaderRoleConnection(team: Team) {
        const predicate = new MethodologyPredicate<RoleConnection>("teamId", "==", team.teamId)
            .and(new MethodologyPredicate<RoleConnection>("role.roleType.code", "==", RoleTypeCode.TeamLeader));

        const roleConnections = await lastValueFrom(this.getRoleConnectionsFromActiveTeamsByPredicate(predicate));
        return ArrayUtilities.getSingleFromArray(roleConnections.filter((r) => r.isActive()));
    }

    @AfterOrganisationInitialisationObservable
    private getRoleConnectionsFromActiveTeamsByPredicate(predicate: MethodologyPredicate<RoleConnection>) {
        return this.commonDataService.getWithOptions(
            RoleConnectionBreezeModel,
            predicate.getKey(RoleConnectionBreezeModel.identifier),
            {
                // As Isaac pointed out, RoleConnections are already primed from getAll Connections in auth service.
                // Don't have to prime again and just use Connection identifier as the encompassing key
                encompassingKey: ConnectionBreezeModel.identifier,
                predicate,
            },
        );
    }

    private getActiveRoleConnectionsForTeamId(connection: Connection, teamId: number) {
        return ArrayUtilities.getSingleFromArray(connection.roleConnections
            .filter((roleConnection) => roleConnection.isActive())
            .filter((roleConnection) => roleConnection.role?.teamId === teamId));
    }

    public async promiseToGetUsedTeamBackgroundColourPresetGroup(limit = 8) {
        const teams = await this.promiseToGetAllActiveTeams();
        const newestTeams = SortUtilities.sortArrayReverseChronological(teams, (t) => t.startDate);
        const teamTypes = await this.promiseToGetTeamTypeConfigurations();

        const otherTeamColours = ArrayUtilities.distinct(ArrayUtilities.withoutNullOrUndefined(newestTeams
            .map((team) => team.extensions.getCustomData()?.backgroundColour)
            .concat(teamTypes.map((tt) => tt.backgroundColour))))
            .slice(0, limit);

        if (otherTeamColours.length === 0) {
            return [];
        }

        return [{
            key: "Background colours in use",
            items: otherTeamColours,
        }] as IColourPickerPresetGroups;
    }

    public async promiseToGetUsedTeamIconColourPresetGroup(limit = 8) {
        const teams = await this.promiseToGetAllActiveTeams();
        const newestTeams = SortUtilities.sortArrayReverseChronological(teams, (t) => t.startDate);
        const teamTypes = await this.promiseToGetTeamTypeConfigurations();

        const otherTeamColours = ArrayUtilities.distinct(ArrayUtilities.withoutNullOrUndefined(newestTeams
            .map((team) => team.extensions.getCustomData()?.iconColour)
            .concat(teamTypes.map((tt) => tt.iconColour))))
            .slice(0, limit);

        return [{
            key: "Icon colours in use",
            items: ArrayUtilities.distinct([
                ...otherTeamColours,
                // always have the default team colour listed as other team colour.
                // resolve the colour so it doesn't duplicate when the default team colour is selected for a team
                ColourUtilities.resolveColour("var(--adapt-team)"),
            ]),
        }] as IColourPickerPresetGroups;
    }

    public async promiseToVerifyPersonActiveInTeam(person: Person, team: Team) {
        const teamMembers = await this.promiseToGetActiveTeamMembers(team);
        return teamMembers.includes(person);
    }

    public async promiseToVerifyTeamRoleHasFeaturePermission(team: Team, roleTypeCode: RoleTypeCode, featurePermission: FeaturePermissionName) {
        const role = await this.promiseToGetTeamRoleByRoleTypeCode(team.teamId, roleTypeCode);
        if (!role) {
            return false;
        }

        return role.extensions.hasPermission(featurePermission);
    }

    public reactivateTeam(team: Team) {
        // we are just reactivating the team and roles. Leaving the other stuff to the user to figure out what they want to do.
        team.endDate = undefined;
        team.roles.forEach((role) => role.endDate = undefined);
        return this.save();
    }

    public async promiseToUpdateRoleConnectionsForTeamLeaderChange(team: Team, newTeamLeaderConnection: Connection, oldTeamLeaderConnection?: Connection) {
        const connections: (RoleConnection | undefined)[] = [];

        if (oldTeamLeaderConnection) {
            this.log.info("Removing old team leader", oldTeamLeaderConnection.person);
            const existingRoleConnection = this.getActiveRoleConnectionsForTeamId(oldTeamLeaderConnection, team.teamId);
            if (existingRoleConnection) {
                connections.push(await this.promiseToChangeToTeamMemberRole(existingRoleConnection, team));
            }
        }

        if (newTeamLeaderConnection) {
            this.log.info("Adding new team leader", newTeamLeaderConnection);
            const existingRoleConnection = this.getActiveRoleConnectionsForTeamId(newTeamLeaderConnection, team.teamId);
            connections.push(await this.promiseToChangeToTeamLeaderRole(team, newTeamLeaderConnection, existingRoleConnection));
        }

        return connections;
    }

    public async promiseToUpdateRoleConnectionForTeamInputChange(team: Team, teamInputConnection: Connection) {
        const existingRoleConnection = this.getActiveRoleConnectionsForTeamId(teamInputConnection, team.teamId);
        if (existingRoleConnection && existingRoleConnection.role.roleType?.code === RoleTypeCode.TeamInput) {
            this.log.info("Removing old team input roleConnection", teamInputConnection.person);
            return this.promiseToChangeToTeamMemberRole(existingRoleConnection, team);
        } else {
            this.log.info("No team input roleConnection found for person", teamInputConnection.person);
        }

        return undefined;
    }

    private async promiseToChangeToTeamLeaderRole(team: Team, newTeamLeaderConnection: Connection, roleConnection?: RoleConnection) {
        let connection: Connection | undefined;

        if (roleConnection) {
            // person was previously a team member, so check if they were accidentally demoted
            this.log.info("New team leader already had an active role connection", roleConnection);
            connection = roleConnection.connection;
            this.removeTeamMember(roleConnection);

            const existingRoleConnections = this.commonDataService.getChangedReferenceEntities(RoleConnectionBreezeModel, "connectionId", connection.connectionId);

            if (existingRoleConnections) {
                const existingTeamLeaderRoleConnection = existingRoleConnections.find((rc) => rc.role?.roleType!.code === RoleTypeCode.TeamLeader);
                if (existingTeamLeaderRoleConnection) {
                    this.log.info("Reusing existing and unsaved old team leader role", existingTeamLeaderRoleConnection);
                    // accidental demotion, just re-instate the old team leader role connection
                    await lastValueFrom(this.commonDataService.rejectChanges(existingTeamLeaderRoleConnection));
                    return existingTeamLeaderRoleConnection;
                }
            }
        }

        // person is not a current team member, just create a new role connection
        this.log.info("Creating new team leader role connection for person", newTeamLeaderConnection);
        return this.promiseToAddTeamLeader(team, connection ?? newTeamLeaderConnection);
    }

    private async promiseToChangeToTeamMemberRole(roleConnection: RoleConnection, team: Team) {
        const connection = roleConnection.connection;
        const isNew = roleConnection.entityAspect.entityState.isAdded();

        this.removeTeamMember(roleConnection, true);

        if (isNew) {
            // team leader hadn't been saved yet - accidental promotion?
            const existingRoleConnections = this.commonDataService.getChangedReferenceEntities(RoleConnectionBreezeModel, "connectionId", connection.connectionId);

            if (existingRoleConnections) {
                const existingTeamMemberRole = existingRoleConnections.find((rc) => rc.role?.roleType!.code === RoleTypeCode.TeamMember);

                if (existingTeamMemberRole) {
                    this.log.info("Reusing existing unsaved team member role", existingTeamMemberRole);
                    // accidental promotion, re-instate the old team member role connection
                    await lastValueFrom(this.commonDataService.rejectChanges(existingTeamMemberRole));
                    return existingTeamMemberRole;
                }
            }

            // don't bother creating a team member role connection if we're creating the team right now or they were selected by accident
            return undefined;
        }

        // create a team member role connection for this demoted team leader
        this.log.info("Creating new team member role connection for demoted person", connection);
        return this.promiseToAddTeamMember(team, connection, connection.userType);
    }

    public removeTeamMember(roleConnection: RoleConnection, dontResetTeamLeader: boolean = false) {
        // we don't need to keep history of team inputs, just straight delete them
        if (roleConnection.role?.roleType?.code === RoleTypeCode.TeamInput) {
            roleConnection.entityAspect.setDeleted();
            return roleConnection;
        }

        if (!dontResetTeamLeader
            && (roleConnection.role && roleConnection.role.roleType
                // this may be used to remove newly added roleConnection without having the role assigned.
                && roleConnection.role.roleType.code === RoleTypeCode.TeamLeader
                && roleConnection.team.teamLeaderPersonId === roleConnection.connection.personId)) {
            roleConnection.team.teamLeaderPersonId = undefined;
        }

        if (roleConnection.entityAspect.entityState.isAdded()) {
            roleConnection.entityAspect.setDeleted();
        } else {
            roleConnection.endDate = moment.utc().toDate();
        }

        return roleConnection;
    }

    public async promiseToEndTeam(team: Team) {
        if (!team.endDate) {
            throw new Error("No team.endDate specified");
        }

        // end the associated roles
        // NOTE: The backend will end the associated roleConnections, no need to do on frontEnd
        for (const role of team.roles) {
            if (!role.endDate) {
                role.endDate = team.endDate;
            }
        }

        team.teamLeaderPersonId = undefined;

        team.boards.forEach((t) => t.isArchived = true);
    }

    public async promiseToRemoveTeam(team: Team) {
        // The server side will remove all related entities to this team
        // We chose to delete on the server side as its less work than fetching every single related entity on the client side prior to the delete
        // We do, however, need to delete the locations, which affects the navigation properties so
        // we need to keep references to the teamLocations and stubs that have the locations to update the
        // ordinals after deletion.
        // parent location ordinals need to be updated, child locations are just removed without issue since they all belong to the removed team itself
        const allTeamLocations = await lastValueFrom(this.getAllLocationsForTeam(team));
        const [teamChildLocations, teamParentLocations] = ArrayUtilities.partition(allTeamLocations, (tl) => tl.parentTeamId === team.teamId);
        const parentTeamLocationStubs = teamParentLocations.map((tl) => ({ zone: tl.zone, parentTeamId: tl.parentTeamId, ordinal: tl.ordinal }));

        await Promise.all([
            ...teamParentLocations.map((tl) => lastValueFrom(this.commonDataService.remove(tl))),
            ...teamChildLocations.map((tl) => lastValueFrom(this.commonDataService.remove(tl))),
            lastValueFrom(this.commonDataService.remove(team)),
        ] as Promise<any>[]);
        await this.promiseToUpdateTeamLocationOrdinalsForRemovedTeamLocations(parentTeamLocationStubs);
    }

    // Get: Team Roles

    public promiseToGetTeamRoles(team: Team, roleTypeCode?: RoleTypeCode) {
        const predicate = new MethodologyPredicate<Role>("teamId", "==", team.teamId);

        if (roleTypeCode) {
            predicate.and(new MethodologyPredicate<Role>("roleType.code", "==", roleTypeCode));
        }

        return lastValueFrom(this.archData.getActiveRolesByPredicate(predicate));
    }

    public async promiseToGetIntersectingTeamsForPeopleWithPermissionToEditPcu(...people: Person[]) {
        people = people.filter(ObjectUtilities.isNotNullOrUndefined);
        const teams = await this.promiseToGetIntersectingTeamsForPeople(...people);
        return teams.filter((team) => people.every((person: Person) => this.personCanParticipateInTeamPcu(person, team)));
    }

    // Get: Team Members

    public async promiseToGetIntersectingTeamsForPeople(...people: Person[]) {
        people = people.filter(ObjectUtilities.isNotNullOrUndefined);
        if (people.length === 0) {
            return this.promiseToGetAllTeams();
        }

        const peopleTeams = await Promise.all(people.map((p) => this.promiseToGetActiveTeamsForPersonId(p.personId)));
        if (peopleTeams.length === 1) {
            return peopleTeams[0];
        }

        const firstGroupOfTeams = peopleTeams.pop();
        return peopleTeams.reduce(ArrayUtilities.intersectArrays, firstGroupOfTeams!);
    }

    public personCanParticipateInTeamPcu(person: Person, team: Team) {
        const connections = person.getActiveConnections();
        return connections.some((c) => c.roleConnections.some((rc) =>
            rc.teamId === team.teamId && rc.role?.extensions.hasPermission(FeaturePermissionName.PeoplePeerCatchUpEdit)));
    }

    public getUniqueRoleConnectionPeople(roleConnections: RoleConnection[]) {
        return ArrayUtilities.distinct(roleConnections.map((roleConnection) => roleConnection.connection.person));
    }

    private promiseToUpdateTeamLocationOrdinalsForRemovedTeamLocations(teamLocations: Partial<TeamLocation>[]) {
        const updateTeamLocationsOrdinals = teamLocations.map(async (teamLocation) => {
            const locationItems = await lastValueFrom(this.getLocationItems(teamLocation));
            SortUtilities.updateIntegerSortedArrayAfterItemRemoval(locationItems, "ordinal");
            return teamLocation;
        });

        return Promise.all(updateTeamLocationsOrdinals);
    }

    public getTeamSupplementaryData(team: Team) {
        return this.commonDataService.getById(TeamSupplementaryDataBreezeModel, team.teamId);
    }

    public getOrCreateTeamSupplementaryData(team: Team) {
        return this.getTeamSupplementaryData(team).pipe(
            switchMap((supplementaryData) => supplementaryData
                ? of(supplementaryData)
                : this.createTeamSupplementaryData(team)),
        );
    }

    public createTeamSupplementaryData(team: Team) {
        return this.commonDataService.create(TeamSupplementaryDataBreezeModel, { team });
    }

    public createTeamTypeConfiguration(initialValues?: Partial<TeamTypeConfiguration>) {
        return from(this.promiseToGetTeamTypeConfigurations()).pipe(
            switchMap((existingConfigs) => {
                const lastOrdinal = Math.max(...existingConfigs.map((t) => t.ordinal), -1);
                return this.commonDataService.create(TeamTypeConfigurationBreezeModel, {
                    organisationId: this.organisationService.getOrganisationId(),
                    ordinal: lastOrdinal + 1,
                    ...initialValues,
                });
            }),
        );
    }

    public addChildTeamForParentTeam(childTeam: Team, parentTeam: Team) {
        return this.getChildTeamLocations(parentTeam).pipe(
            switchMap((locations) => {
                // -1 as fallback for when no other locations present
                return this.commonDataService.create(TeamLocationBreezeModel, {
                    team: childTeam,
                    parentTeam,
                    ordinal: this.getNextTeamLocationOrdinal(locations),
                });
            }),
        );
    }

    private getNextTeamLocationOrdinal(sortedLocations: TeamLocation[]) {
        return Math.max(-1, ...sortedLocations.map((i) => i.ordinal)) + 1;
    }

    public applyTeamsFilter(teams: Team[], filterFunction?: (teams: Team[]) => Team[] | Observable<Team[]>) {
        if (!teams) {
            return of([]);
        }

        if (!filterFunction) {
            return of(teams);
        }

        const filtered = filterFunction(teams);
        return Array.isArray(filtered)
            ? of(filtered)
            : filtered;
    }
}
